import { maxBy, sortedUniq } from 'lodash';
import { RubberbandMetrics } from 'types/configuration';
import {
  FileHeaderChildData,
  FileHeaderData,
  GetFileHeaderDataResponse,
  GetQaTablesFieldsListResponse,
  QaTableCellsDataType,
  QcTableRowData,
  RectMetricsForRubberBandType
} from 'types/image-viewer';
import { VerificationTechniquesLabelEnum, VerificationTechniquesType } from 'types/verification';

export const generateMetricsFromROI = (roi: string): RectMetricsForRubberBandType | null => {
  if (roi) {
    const roiSplit: string[] = roi.split(',');
    if (roiSplit.length === 4) {
      const [left, top, width, height] = roiSplit.map((val) => Number(val));
      if (left || top || width || height) {
        return { left, top, width, height };
      }
    }
  }
  return null;
};

export const generateROIStringFromMetrics = (metrics: RubberbandMetrics): string =>
  metrics ? `${metrics.startX},${metrics.startY},${metrics.width},${metrics.height}` : '';

export const convertFieldChildDataToHeaderData = (
  headerData: FileHeaderData,
  childData?: FileHeaderChildData[]
): GetFileHeaderDataResponse['headerData'][0] => {
  if (childData) {
    const convertedChildData: FileHeaderData[] = childData.map((data) => ({
      ...headerData,
      isChild: true,
      childID: data.childID,
      fieldID: data.fieldID,
      fileID: data.fileID,
      resultID: Number(data.resultID || 0),
      extractedResult: data.extractedResult,
      confidenceScore: data.confidenceScore,
      confidenceFlag: data.confidenceFlag,
      pageNumber: data.pageNumber,
      extractedROI: data.resultROI,
      fieldName: data.patternValue,
      fieldValue: data.updatedResult
    }));
    return { ...headerData, childData: convertedChildData };
  } else {
    return { ...headerData, childData: [] };
  }
};

export const convertTableFieldsListToUI = (fieldsList: GetQaTablesFieldsListResponse[]): QcTableRowData => {
  const valueMap = new Map();
  fieldsList.forEach(({ rowID, columnID, ...rest }) => {
    const placeValue = `${rowID}-${columnID}`;
    if (valueMap.has(placeValue)) {
    }
    valueMap.set(placeValue, { ...rest, rowID, columnID });
  });

  const maxRow: number = Number(maxBy(fieldsList, 'rowID')?.rowID || 0);
  const maxColumn: number = Number(maxBy(fieldsList, 'columnID')?.columnID || 0);

  const tableRows: QaTableCellsDataType[] = [];
  for (let i = 0; i < maxRow; i++) {
    const cellData: QaTableCellsDataType = {};
    for (let j = 0; j < maxColumn; j++) {
      const key = `${i + 1}-${j + 1}`;
      const cellValue: GetQaTablesFieldsListResponse = valueMap.get(key);
      cellData[`key_${j + 1}`] = key;
      cellData[`value_${j + 1}`] = cellValue.fieldValue;
      cellData.rowID = cellValue.rowID;
      cellData.rowStatus = cellValue.rowStatus;
    }
    tableRows.push(cellData);
  }

  return {
    tableDataMap: valueMap,
    tableRows,
    original: fieldsList
  };
};

export const isArrayHasConsecutivveNumbers = (numbers: string[] | number[]): boolean => {
  const sortedNumber = sortedUniq(numbers.map((val) => Number(val)).sort());
  for (let i = 0; i < sortedNumber.length; i++) {
    if (i === 0) continue;
    if (sortedNumber[i - 1] + 1 !== sortedNumber[i]) {
      return false;
    }
  }
  return true;
};

const SampleExpressionOutput = (expressionText: string): string => {
  // keep this as a callback if it's used in other hooks or to avoid recalculations
  let sampleText = '';
  let Bracket = false;
  const arr = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  for (let i = 0; i < expressionText.length; i++) {
    const char = expressionText.charAt(i);
    if (char === 'A' && !Bracket) {
      sampleText += arr[Math.floor(Math.random() * arr.length)];
    } else if (char === 'N' && !Bracket) {
      sampleText += Math.floor(Math.random() * 9) + 1;
    } else {
      if (char === '[') {
        Bracket = true;
        continue;
      } else if (char === ']') {
        Bracket = false;
        continue;
      } else if (char === '\n' || char === '\r') {
        sampleText += char;
      }

      if (Bracket) {
        sampleText += char;
      }
    }
  }
  if (Bracket) {
    return 'Error';
  }
  return sampleText;
}; // Memoize to avoid recalculations

export const convertVerificationExpressPattern: (_val: string) => { expressionPattern: string; expressionRegex: string } = (
  value: string
) => {
  const returnData = {
    expressionPattern: '',
    expressionRegex: ''
  };
  const newPattern = value || '';
  let transformedValue = '';
  let insideBrackets = false;

  // Iterate over each character in the new input
  for (let i = 0; i < newPattern.length; i++) {
    const char = newPattern[i];

    if (char === '[') {
      // Allow entering the opening bracket and everything after it until a closing bracket
      insideBrackets = true;
      transformedValue += char;
    } else if (char === ']') {
      // Allow the closing bracket and switch back to pattern validation mode
      insideBrackets = false;
      transformedValue += char;
    } else if (char === '\n') {
      // Allow newline characters
      transformedValue += char;
    } else if (insideBrackets) {
      // Allow any character while inside brackets
      transformedValue += char;
    } else if (/[an]/i.test(char)) {
      // Convert lowercase 'a' and 'n' to uppercase 'A' and 'N' for pattern part
      transformedValue += char.toUpperCase();
    }
    // Ignore any character that doesn't match the pattern or is outside brackets
  }
  const sampleOutput = SampleExpressionOutput(transformedValue);

  returnData.expressionPattern = transformedValue;
  returnData.expressionRegex = sampleOutput;

  return returnData;
};

export const getTechniqueType = (techniqueName: string): VerificationTechniquesType | null => {
  techniqueName = techniqueName.toUpperCase();
  for (let techniqueType in VerificationTechniquesLabelEnum) {
    if (techniqueName.includes(techniqueType.toUpperCase())) {
      return techniqueType as VerificationTechniquesType;
    }
  }
  return null;
};

export const assignRef =
  <T>(...refs: (React.Ref<T> | undefined)[]): ((instance: T | null) => void) =>
  (instance: T | null) => {
    refs.forEach((ref) => {
      if (!ref) return;
      if (typeof ref === 'function') {
        ref(instance);
      } else {
        // @ts-ignore
        ref.current = instance;
      }
    });
  };
